<script lang="ts">
  import { debounce } from 'lodash-es'
  import { setContext } from 'svelte'
  import app from '#app/app'
  import type { FlashState } from '#app/lib/components/flash.svelte'
  import Link from '#app/lib/components/link.svelte'
  import SubEntitiesBrowser from '#entities/components/layouts/sub_entities_browser.svelte'
  import { getSubEntitiesSections, type SubEntitiesBrowserSectionData } from '#entities/components/lib/entities'
  import { workAuthorProperties } from '#entities/lib/editor/properties_per_subtype'
  import type { SerializedEntity } from '#entities/lib/entities'
  import Spinner from '#general/components/spinner.svelte'
  import type { AbsoluteUrl } from '#server/types/common'
  import { i18n } from '#user/lib/i18n'
  import AuthorsInfo from './authors_info.svelte'
  import BaseLayout from './base_layout.svelte'
  import EntityTitle from './entity_title.svelte'
  import Infobox from './infobox.svelte'

  let {
    entity = $bindable(),
  }: {
    entity: SerializedEntity
  } = $props()

  // @ts-expect-error TODO: recover setting entity.wikisource in serializeEntity
  const { claims, wikisource } = entity

  setContext('layout-context', 'article')

  let href: AbsoluteUrl | undefined = $state()
  const DOI = claims['wdt:P356']?.[0]
  if (DOI != null) href = `https://dx.doi.org/${DOI}`

  let sections: SubEntitiesBrowserSectionData[] | undefined = $state()
  let waitingForReverseEntities = $state()
  let flash: FlashState | undefined = $state()
  function getSections () {
    waitingForReverseEntities = getSubEntitiesSections({ entity })
      .then(res => sections = res)
      .catch(err => flash = err)
    app.navigate(`/entity/${entity.uri}`)
  }
  const lazyGetSections = debounce(getSections, 100)
  $effect(() => {
    if (entity) lazyGetSections()
  })
</script>

<BaseLayout
  {entity}
  bind:flash
>
  {#snippet entityLayoutMain()}
    <div class="entity-layout">
      <div class="top-section">
        <div class="work-section">
          <EntityTitle {entity} hasLinkTitle={href != null} {href} />
          <AuthorsInfo {claims} />
          <Infobox
            {claims}
            omittedProperties={workAuthorProperties}
            entityType={entity.type}
          />
          {#if wikisource}
            <Link
              url={wikisource.url}
              text={i18n('on Wikisource')}
              icon="wikisource"
              classNames="wikisource"
            />
          {/if}
        </div>
      </div>
      <div class="relatives-browser">
        {#await waitingForReverseEntities}
          <Spinner center={true} />
        {:then}
          <SubEntitiesBrowser {sections} />
        {/await}
      </div>
      <!-- TODO: add RelativeEntitiesList "cited by" -->
    </div>
  {/snippet}
</BaseLayout>

<style lang="scss">
  @import "general/scss/utils";
  .entity-layout{
    @include display-flex(column, center);
    inline-size: 100%;
  }
  .top-section{
    @include display-flex(row, flex-start, center);
    inline-size: 100%;
  }
  .work-section{
    @include display-flex(column, flex-start);
    flex: 1 0 0;
    margin: 0 1em;
    :global(.claims-infobox-wrapper){
      margin-block-end: 1em;
    }
    :global(.wikisource){
      margin: 0.5em 0;
    }
  }
  .relatives-browser{
    margin: 1em 0;
    inline-size: 100%;
  }
  /* Small screens */
  @media screen and (width < $small-screen){
    .work-section{
      margin-inline-start: 0;
      :global(.claims-infobox-wrapper){
        margin-block-end: 0;
      }
    }
    .top-section{
      @include display-flex(column, center);
    }
    .entity-layout{
      @include display-flex(column);
    }
  }
</style>
